import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import React from 'react'

/**
 * Builds a map from "partType|index" key -> contentItem id for every article
 * (textType === 'Article') reachable through the DTV. The key is the stable
 * identifier for an article across not-yet-added and project DTVs — partType is set on the
 * dtv item and index is the 0-based position of the content item within
 * documentTemplateViewContentItems (including hidden items).
 *
 * Using list position rather than paragraphOrder is intentional: the backend assigns
 * paragraphOrder only to visible articles and renumbers when items become hidden.
 * When the first product is added to a not-yet-added family some articles become hidden
 * (e.g. "Source Limitations" → _._), shifting all subsequent paragraphOrders by one
 * and causing the migration to map the wrong article as expanded. List position is
 * unaffected by visibility changes.
 *
 * Returns an empty map for missing inputs.
 */
export const buildArticleKeyToIdMap = (
    contentItemNodes: Partial<ContentItem>[] | undefined,
    dtv: Partial<DocumentTemplateView> | null | undefined
): Map<string, string> => {
    const map = new Map<string, string>()
    if (!dtv?.items || !contentItemNodes) return map
    const articleIds = new Set<string>()
    for (const node of contentItemNodes) {
        if (node?.textType === 'Article' && node?.id) articleIds.add(node.id)
    }
    for (const item of dtv.items) {
        const partType = item?.partType
        if (!partType) continue
        const contentItems =
            item.documentTemplateViewContents?.[0]
                ?.documentTemplateViewContentItems ?? []
        contentItems.forEach((ci, index) => {
            if (ci?.id && articleIds.has(ci.id)) {
                map.set(`${partType}|${index}`, ci.id)
            }
        })
    }
    return map
}

/**
 * Re-maps a list of expanded article ids across a contentItemNodes / dtv refetch so
 * articles the user had expanded before the change stay expanded after.
 *
 * The migration only runs when `dtv.id` actually changes — most notably the
 * adding-to-project PT case (Division/Section/PT not yet in project), where
 * every article content item gets a new project-scoped id. Within a single
 * `dtv.id` lifetime, content-item ids are stable and no migration is needed; gating
 * on `dtv.id` avoids paying the map-build cost on every unrelated reactive-var write.
 *
 * Articles are identified by the stable cross-DTV key "partType|paragraphOrder"
 * (see {@link buildArticleKeyToIdMap}).
 */
export const useExpandedArticlesMigration = (
    contentItemNodes: Partial<ContentItem>[] | undefined,
    dtv: Partial<DocumentTemplateView> | null | undefined,
    setExpandedArticlesList: React.Dispatch<React.SetStateAction<string[]>>
): void => {
    const dtvId = dtv?.id
    const previousDtvId = usePrevious(dtvId)
    const previousContentItemNodes = usePrevious(contentItemNodes)
    const previousDtv = usePrevious(dtv)

    React.useEffect(() => {
        if (!previousDtvId || !dtvId || previousDtvId === dtvId) return
        if (!previousContentItemNodes || !previousDtv) return
        setExpandedArticlesList((prev) => {
            if (prev.length === 0) return prev
            const oldIdToKey = new Map<string, string>()
            for (const [key, id] of buildArticleKeyToIdMap(
                previousContentItemNodes,
                previousDtv
            )) {
                oldIdToKey.set(id, key)
            }
            if (oldIdToKey.size === 0) return prev
            const newKeyToId = buildArticleKeyToIdMap(contentItemNodes, dtv)
            if (newKeyToId.size === 0) return prev
            let changed = false
            const seen = new Set<string>()
            const migrated: string[] = []
            for (const oldId of prev) {
                const key = oldIdToKey.get(oldId)
                const newId = key ? newKeyToId.get(key) : undefined
                const finalId = newId ?? oldId
                if (newId && newId !== oldId) changed = true
                if (!seen.has(finalId)) {
                    seen.add(finalId)
                    migrated.push(finalId)
                }
            }
            if (!changed && migrated.length === prev.length) return prev
            return migrated
        })
    }, [
        dtvId,
        previousDtvId,
        contentItemNodes,
        dtv,
        previousContentItemNodes,
        previousDtv,
        setExpandedArticlesList,
    ])
}
